import { IVec2, Vec2 } from "./Vec2";

export function getLineIntersection(x1: number, y1: number, x2: number, y2: number, x3: number, y3: number, x4: number, y4: number, skipLineCheck?: boolean): { x: number; y: number; ua: number; ub: number } | null {
  // Check if none of the lines are of length 0
  if ((x1 === x2 && y1 === y2) || (x3 === x4 && y3 === y4)) {
    return null;
  }

  const denominator = (y4 - y3) * (x2 - x1) - (x4 - x3) * (y2 - y1);

  // Lines are parallel
  if (denominator === 0) {
    return null;
  }

  const ua = ((x4 - x3) * (y1 - y3) - (y4 - y3) * (x1 - x3)) / denominator;
  const ub = ((x2 - x1) * (y1 - y3) - (y2 - y1) * (x1 - x3)) / denominator;

  // is the intersection along the segments
  if (!skipLineCheck) {
    if (ua < 0 || ua > 1 || ub < 0 || ub > 1) {
      return null;
    }
  }

  // Return a object with the x and y coordinates of the intersection
  const x = x1 + ua * (x2 - x1);
  const y = y1 + ua * (y2 - y1);

  return { x, y, ua, ub };
}

export function getRayIntersection(x1: number, y1: number, dirX1: number, dirY1: number, x2: number, y2: number, dirX2: number, dirY2: number): { x: number; y: number; ua: number; ub: number } | null {
  const denominator = dirY2 * dirX1 - dirX2 * dirY1;

  // Lines are parallel
  if (denominator === 0) {
    return null;
  }

  const ua = (dirX2 * (y1 - y2) - dirY2 * (x1 - x2)) / denominator;
  const ub = (dirX1 * (y1 - y2) - dirY1 * (x1 - x2)) / denominator;

  // Return a object with the x and y coordinates of the intersection
  const x = x1 + ua * dirX1;
  const y = y1 + ua * dirY1;

  return { x, y, ua, ub };
}

export class Line2 {
  public from: IVec2;
  public to: IVec2;

  public constructor(_f: IVec2, _t: IVec2) {
    this.from = _f;
    this.to = _t;
  }

  public getIntersection(otherLine: Line2): IVec2 | null {
    const ret = getLineIntersection(this.from.x, this.from.y, this.to.x, this.to.y, otherLine.from.x, otherLine.from.y, otherLine.to.x, otherLine.to.y);
    if (!ret) return null;
    return Vec2.from(ret.x, ret.y);
  }

  public getRayIntersection(otherLine: Line2): IVec2 | null {
    const ret = getRayIntersection(
      this.from.x,
      this.from.y,
      this.to.x - this.from.x,
      this.to.y - this.from.y,
      otherLine.from.x,
      otherLine.from.y,
      otherLine.to.x - otherLine.from.x,
      otherLine.to.y - otherLine.from.y
    );
    if (!ret) return null;
    return Vec2.from(ret.x, ret.y);
  }

  public getNearestPoint(otherPoint: IVec2, useRay?: boolean): IVec2 {
    const dist2 = (p1: IVec2, p2: IVec2): number => (p1.x - p2.x) * (p1.x - p2.x) + (p1.y - p2.y) * (p1.y - p2.y);
    const from = this.from;
    const to = this.to;
    const l2 = dist2(from, to);
    if (l2 <= 0.0000001) return this.from;

    const t = ((otherPoint.x - from.x) * (to.x - from.x) + (otherPoint.y - from.y) * (to.y - from.y)) / l2;
    if (useRay !== true) {
      if (t < 0) return from;
      if (t > 1) return to;
    }

    return Vec2.from(from.x + t * (to.x - from.x), from.y + t * (to.y - from.y));
  }
}
